import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { AnimatePresence } from "./AnimatePresenceWrapper";
import {
  X,
  ShoppingCart,
  Ticket,
  CheckCircle2,
  Loader,
  CreditCard,
  AlertCircle,
  Zap,
} from "lucide-react";

// @ts-ignore
import paystackLogo from "../assets/img/paystack.svg";
// @ts-ignore
import stripeLogo from "../assets/img/stripe.svg";
import { toast } from "sonner";
import { PaystackButton } from "react-paystack";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { PerformRequest } from "../lib/usePerformRequest";
import { Endpoints } from "../lib/endpoints";
import { HttpCodes } from "../lib/data";
import { useNavigate } from "react-router-dom";
import { NonPaginatedResponse } from "../types";
import { useUser } from "../context/UserContext";
import { ENV } from "../lib/env";
import { getCurrencySymbol } from "../lib/data/currencies";
import { getMyTicketsUrl } from "../lib/utils/domain";

interface TicketTier {
  id: string;
  name: string;
  price: number;
  description: string;
  remaining: number;
  total: number;
  features: string[];
  color: string;
  popular?: boolean;
  currency?: string;
}

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: number;
  eventTitle: string;
  selectedTickets: Record<string, number>;
  ticketTiers: TicketTier[];
  totalAmount: number;
  totalTickets: number;
  organizationSlug: string;
  isPaidEvent?: boolean;
}

interface PaymentMethodData {
  price: number;
  currency: string;
  reference: string;
  stripe_client_id: string;
  stripe_publishable_key: string;
}

export function CheckoutModal({
  isOpen,
  onClose,
  eventId,
  eventTitle,
  selectedTickets,
  ticketTiers,
  totalAmount,
  totalTickets,
  organizationSlug,
  isPaidEvent = false,
}: CheckoutModalProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPaystackButton, setShowPaystackButton] = useState(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [paymentMethodData, setPaymentMethodData] =
    useState<PaymentMethodData | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    "paystack" | "stripe" | null
  >(null);
  const [showOtherPaymentMethods, setShowOtherPaymentMethods] = useState(false);
  const requiresPayment = totalAmount > 0;
  const navigate = useNavigate();
  const { userProfile } = useUser();
  const paystackWrapperRef = useRef<HTMLDivElement>(null);

  // Get currency from first ticket tier, or default to USD
  const currencyCode =
    ticketTiers.length > 0 && ticketTiers[0].currency
      ? ticketTiers[0].currency
      : "USD";

  const currencySymbol = getCurrencySymbol(currencyCode);

  // Helper function to determine default payment method based on location
  // Nigeria (NG) defaults to Paystack, all others default to Stripe
  const getDefaultPaymentMethod = (
    countryCode: string | undefined
  ): "paystack" | "stripe" => {
    return countryCode?.toUpperCase() === "NG" ? "paystack" : "stripe";
  };

  // Cache for IP/location info from ipinfo.io
  const ipInfoRef = useRef<any | null>(null);
  const [ipInfo, setIpInfo] = useState<any | null>(null);

  const getIpInfo = async () => {
    // Only fetch once per session in this component
    if (ipInfoRef.current) return ipInfoRef.current;
    if (!ENV.IP_INFO_TOKEN) return null;

    try {
      const response = await fetch(
        `https://ipinfo.io/json?token=${ENV.IP_INFO_TOKEN}`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch IP information");
      }
      const data = await response.json();
      ipInfoRef.current = data;
      setIpInfo(data);
      return data;
    } catch (error) {
      console.error("Error fetching IP info:", error);
      // We don't block payment if IP lookup fails
      return null;
    }
  };

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      // Preload IP/location when checkout opens
      getIpInfo().catch(() => {});
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Paystack configuration
  // If currency is NGN, use amount directly and convert to kobo (multiply by 100)
  // Otherwise, convert USD to Naira (1444.86) then to kobo (multiply by 100)
  const USD_TO_NAIRA_RATE = 1444.86;
  const getPaystackAmount = () => {
    // Use paymentMethodData if available (from API), otherwise use ticket data
    const amount = paymentMethodData?.price || totalAmount;
    const currency = paymentMethodData?.currency || currencyCode;

    if (currency.toUpperCase() === "NGN") {
      // Currency is already in Naira, convert to kobo
      return Math.round(amount * 100);
    } else {
      // Convert from other currency (e.g., USD) to Naira then to kobo
      return Math.round(amount * USD_TO_NAIRA_RATE * 100);
    }
  };

  const paystackConfig = {
    reference: new Date().getTime().toString(),
    email: userProfile?.user?.email || "customer@example.com",
    amount: getPaystackAmount(),
    publicKey: ENV.PAYSTACK_PUBLIC_TEST || "",
  };

  // Paystack payment handlers
  const handlePaystackSuccess = (reference: any) => {
    // Backend webhook handles payment confirmation, so just navigate to my-tickets
    toast.success("Payment successful!");
    onClose(); // Close the checkout modal
    navigate(getMyTicketsUrl(organizationSlug));
  };

  const handlePaystackClose = () => {
    toast.error("Payment cancelled");
    setShowPaystackButton(false);
    // Return user to payment method selection
    setShowPaymentMethodModal(true);
  };

  const handleContinueToPayment = async () => {
    if (!requiresPayment) {
      // If no payment required, directly call handlePurchase
      handlePurchase();
      return;
    }

    // For paid events, first open the payment selection modal.
    // The actual buy endpoint (with payment_option) will be called
    // after the user chooses a payment method.
    if (isPaidEvent) {
      try {
        const ipInfo = await getIpInfo();
        const countryCode = ipInfo?.country;
        const defaultMethod = getDefaultPaymentMethod(countryCode);
        setSelectedPaymentMethod(defaultMethod);
        setShowOtherPaymentMethods(false);
        setShowPaymentMethodModal(true);
      } catch {
        // If IP lookup fails, still open the modal with a sensible default
        setSelectedPaymentMethod("stripe");
        setShowOtherPaymentMethods(false);
        setShowPaymentMethodModal(true);
      }
    } else {
      // For non-paid events, directly show Paystack
      setShowPaystackButton(true);
    }
  };

  // Auto-click Paystack button when it's rendered
  React.useEffect(() => {
    if (showPaystackButton && paystackWrapperRef.current) {
      // Small delay to ensure button is rendered
      const timer = setTimeout(() => {
        const paystackBtn = paystackWrapperRef.current?.querySelector(
          "button"
        ) as HTMLButtonElement;
        if (paystackBtn) {
          paystackBtn.click();
          setShowPaystackButton(false);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showPaystackButton]);

  const handlePurchase = async () => {
    setIsProcessing(true);

    try {
      // Build the payload for FREE events (no payment_option needed)
      const payload = {
        event_id: eventId,
        data: Object.entries(selectedTickets).map(([tierId, quantity]) => ({
          id: parseInt(tierId),
          quantity: quantity,
        })),
      };

      const response = await PerformRequest<
        NonPaginatedResponse<{
          message?: string;
          tickets?: Array<{
            id: number;
            event: number;
            user: number;
            ticket_type: string;
            unique_code: string;
            price: string;
            currency: string;
          }>;
        }>
      >({
        method: Endpoints.GetBuyTickets().method,
        route: Endpoints.GetBuyTickets().route,
        data: payload,
        doNotUseAuthorization: false,
        organizationSlug: organizationSlug,
      });

      if (response.status === HttpCodes.Created) {
        // FREE EVENT
        toast.success(response.data?.message || "Ticket purchase successful!");
        console.log("response.data:", response.data.data);
        // Navigate to receipt page with ticket data
        navigate(getMyTicketsUrl(organizationSlug));
      } else if (response.status === HttpCodes.Success) {
        // PAID EVENT - This should not happen here as we handle it via payment methods
        toast.error("Unexpected response. Please try again.");
      } else {
        toast.error(response.message || "Purchase failed. Please try again.");
      }
    } catch (error: any) {
      console.error("Purchase error:", error);
      toast.error(error.message || "Purchase failed. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePaymentMethodSelection = (method: "paystack" | "stripe") => {
    // Only select the method; actual payment initialization happens
    // when the user clicks the Continue button.
    setSelectedPaymentMethod(method);
  };

  const handleConfirmPayment = async () => {
    if (!selectedPaymentMethod) return;

    setIsProcessing(true);
    try {
      const ipInfo = await getIpInfo();

      // Build payload according to new spec, including payment_option
      const payload = {
        event_id: eventId,
        payment_option: selectedPaymentMethod,
        data: Object.entries(selectedTickets).map(([tierId, quantity]) => ({
          id: parseInt(tierId),
          quantity: quantity,
        })),
        ip_info: ipInfo || undefined,
      };

      const response = await PerformRequest<{
        data?: PaymentMethodData;
      }>({
        method: Endpoints.GetBuyTickets().method,
        route: Endpoints.GetBuyTickets().route,
        data: payload,
        doNotUseAuthorization: false,
        organizationSlug: organizationSlug,
      });

      if (response.status === HttpCodes.Success && response.data?.data) {
        setPaymentMethodData(response.data.data);
        setShowPaymentMethodModal(false);
        setShowOtherPaymentMethods(false);

        if (selectedPaymentMethod === "paystack") {
          // Trigger Paystack checkout
          setShowPaystackButton(true);
        }
        // For Stripe, the StripePaymentForm will open automatically
        // because selectedPaymentMethod === "stripe" and paymentMethodData is set
      } else {
        toast.error(
          response.message ||
            "Failed to initialize payment. Please try again."
        );
      }
    } catch (error: any) {
      console.error("Payment initialization error:", error);
      toast.error(
        error.message || "Failed to initialize payment. Please try again."
      );
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative bg-gradient-to-br from-[#17265a] via-[#1f2f6b] to-[#2d3f7a] p-6 text-white">
              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                className="absolute top-4 right-4 w-8 h-8 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5" />
              </motion.button>

              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-xl flex items-center justify-center border border-white/20">
                  <ShoppingCart className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Checkout</h2>
                  <p className="text-white/80 text-sm">Review your order</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Event Info */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <h3 className="text-[#17265a] font-semibold mb-2">
                  {eventTitle}
                </h3>
                <p className="text-sm text-gray-600">Event ID: {eventId}</p>
              </div>

              {/* Order Summary */}
              <div>
                <h3 className="text-[#17265a] font-semibold mb-4 flex items-center gap-2">
                  <Ticket className="w-5 h-5" />
                  Order Summary
                </h3>
                <div className="space-y-3">
                  {Object.entries(selectedTickets).map(([tierId, quantity]) => {
                    const tier = ticketTiers.find((t) => t.id === tierId);
                    if (!tier) return null;

                    return (
                      <motion.div
                        key={tierId}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-[#17265a]/30 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-gray-900">
                                {tier.name}
                              </h4>
                              {tier.popular && (
                                <span className="px-2 py-0.5 bg-[#17265a] text-white text-xs rounded-full">
                                  Popular
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-2">
                              {tier.description}
                            </p>
                            <div className="text-sm text-gray-500">
                              Quantity:{" "}
                              <span className="font-semibold">{quantity}</span>
                            </div>
                          </div>
                          <div className="text-right ml-4">
                            <div className="text-lg font-bold text-[#17265a]">
                              {getCurrencySymbol(tier.currency)}
                              {(tier.price * quantity).toFixed(2)}
                            </div>
                            <div className="text-sm text-gray-500">
                              {getCurrencySymbol(tier.currency)}
                              {tier.price.toFixed(2)} each
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer with Total and Purchase Button */}
            <div className="border-t border-gray-200 bg-gray-50 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-gray-600">Total Tickets</p>
                  <p className="text-xl font-bold text-gray-900">
                    {totalTickets}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Total Amount</p>
                  <p className="text-2xl font-bold text-[#17265a]">
                    {/* {currencySymbol} */}
                    {totalAmount.toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Detected Location */}
              {ipInfo && (
                <div className="mb-4 rounded-lg bg-white border border-gray-200 p-3 text-xs text-gray-600 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-[#17265a] mt-0.5" />
                  <div>
                    <p className="font-semibold text-gray-700">
                      Detected location for this payment
                    </p>
                    <p>
                      {[ipInfo.city, ipInfo.region, ipInfo.country]
                        .filter(Boolean)
                        .join(", ") || "Location unavailable"}
                      {ipInfo.ip ? ` • IP: ${ipInfo.ip}` : ""}
                    </p>
                  </div>
                </div>
              )}

              <motion.button
                onClick={handleContinueToPayment}
                disabled={isProcessing}
                whileHover={!isProcessing ? { scale: 1.02 } : {}}
                whileTap={!isProcessing ? { scale: 0.98 } : {}}
                className={`w-full py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg ${
                  isProcessing
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-[#17265a] text-white hover:bg-[#0f1a3f] hover:shadow-xl"
                }`}
              >
                {isProcessing ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    <span>Continue to Payment</span>
                  </>
                )}
              </motion.button>

              <div className="mt-3 space-y-1 text-center text-xs text-gray-500">
                <p>By confirming, you agree to our terms and conditions.</p>
              </div>

              {/* Hidden Paystack Button */}
              {requiresPayment && showPaystackButton && (
                <div ref={paystackWrapperRef} className="hidden">
                  <PaystackButton
                    {...paystackConfig}
                    text="Pay"
                    onSuccess={handlePaystackSuccess}
                    onClose={handlePaystackClose}
                  />
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Payment Method Selection Modal */}
      <AnimatePresence>
        {showPaymentMethodModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
            onClick={() => {
              setShowPaymentMethodModal(false);
              setShowOtherPaymentMethods(false);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="relative bg-gradient-to-br from-[#17265a] via-[#1f2f6b] to-[#2d3f7a] p-6 text-white">
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => {
                    setShowPaymentMethodModal(false);
                    setShowOtherPaymentMethods(false);
                  }}
                  className="absolute top-4 right-4 w-8 h-8 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors"
                >
                  <X className="w-5 h-5" />
                </motion.button>

                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-xl flex items-center justify-center border border-white/20">
                    <CreditCard className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Payment Method</h2>
                    <p className="text-white/80 text-sm">
                      {selectedPaymentMethod === "paystack"
                        ? "Paystack is selected for Nigeria"
                        : "Stripe is selected for international payments"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="mb-4 text-center">
                  <p className="text-base font-semibold text-gray-900 mb-1">
                    Total Amount: {currencyCode} {totalAmount.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-600">
                    {selectedPaymentMethod === "paystack"
                      ? "Continue with Paystack"
                      : "Continue with Stripe"}
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Active Payment Method */}
                  {selectedPaymentMethod && (
                    <motion.button
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      onClick={() =>
                        handlePaymentMethodSelection(selectedPaymentMethod)
                      }
                      className={`relative w-full px-4 py-3 border-2 rounded-xl transition-all bg-white hover:shadow-md group ${
                        selectedPaymentMethod === "paystack"
                          ? "border-[#17265a]"
                          : "border-[#635BFF]"
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform p-2 flex-shrink-0">
                          <img
                            src={
                              selectedPaymentMethod === "paystack"
                                ? paystackLogo
                                : stripeLogo
                            }
                            alt={
                              selectedPaymentMethod === "paystack"
                                ? "Paystack"
                                : "Stripe"
                            }
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <div className="flex-1 text-left">
                          <h3 className="text-base font-semibold text-gray-900 mb-1">
                            {selectedPaymentMethod === "paystack"
                              ? "Paystack"
                              : "Stripe"}
                          </h3>
                          <p className="text-xs text-gray-600">
                            {selectedPaymentMethod === "paystack"
                              ? "Pay with cards, bank transfer, or mobile money"
                              : "Pay with credit or debit card"}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-500 flex-shrink-0">
                          {selectedPaymentMethod === "paystack" ? (
                            <>
                              <Zap className="w-4 h-4" />
                              <span>Fast & Secure</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className="w-4 h-4" />
                              <span>Global Payment</span>
                            </>
                          )}
                        </div>
                      </div>
                    </motion.button>
                  )}

                  {/* Toggle to show other payment methods */}
                  {!showOtherPaymentMethods && (
                    <div className="text-center pt-1">
                      <button
                        onClick={() => setShowOtherPaymentMethods(true)}
                        className="text-sm text-[#17265a] hover:text-[#0f1a3f] underline transition-colors"
                      >
                        Use a different payment method
                      </button>
                    </div>
                  )}

                  {/* Other Payment Methods (Hidden by default) */}
                  {showOtherPaymentMethods && selectedPaymentMethod && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="pt-4 border-t border-gray-200"
                    >
                      <p className="text-sm font-semibold text-gray-700 mb-4 text-center">
                        Other Payment Methods
                      </p>
                      <motion.button
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        onClick={() => {
                          const otherMethod =
                            selectedPaymentMethod === "paystack"
                              ? "stripe"
                              : "paystack";
                          setSelectedPaymentMethod(otherMethod);
                          setShowOtherPaymentMethods(false);
                        }}
                        className={`relative w-full px-4 py-3 border-2 border-gray-200 rounded-xl transition-all bg-white hover:shadow-md group hover:${
                          selectedPaymentMethod === "paystack"
                            ? "border-[#635BFF]"
                            : "border-[#17265a]"
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform p-2 flex-shrink-0">
                            <img
                              src={
                                selectedPaymentMethod === "paystack"
                                  ? stripeLogo
                                  : paystackLogo
                              }
                              alt={
                                selectedPaymentMethod === "paystack"
                                  ? "Stripe"
                                  : "Paystack"
                              }
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <div className="flex-1 text-left">
                            <h3 className="text-base font-semibold text-gray-900 mb-1">
                              {selectedPaymentMethod === "paystack"
                                ? "Stripe"
                                : "Paystack"}
                            </h3>
                            <p className="text-xs text-gray-600">
                              {selectedPaymentMethod === "paystack"
                                ? "Pay with credit or debit card"
                                : "Pay with cards, bank transfer, or mobile money"}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-500 flex-shrink-0">
                            {selectedPaymentMethod === "paystack" ? (
                              <>
                                <CheckCircle2 className="w-4 h-4" />
                                <span>Global Payment</span>
                              </>
                            ) : (
                              <>
                                <Zap className="w-4 h-4" />
                                <span>Fast & Secure</span>
                              </>
                            )}
                          </div>
                        </div>
                      </motion.button>
                    </motion.div>
                  )}
                  {/* Confirm / Continue button */}
                  <div className="pt-2">
                    <button
                      type="button"
                      disabled={!selectedPaymentMethod || isProcessing}
                      onClick={handleConfirmPayment}
                      className={`w-full py-3 rounded-lg flex items-center justify-center gap-2 text-sm font-semibold transition-colors ${
                        !selectedPaymentMethod || isProcessing
                          ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                          : "bg-[#17265a] text-white hover:bg-[#0f1a3f]"
                      }`}
                    >
                      {isProcessing ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          <span>Initializing payment...</span>
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4" />
                          <span>Continue</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stripe Payment Form */}
      {selectedPaymentMethod === "stripe" && paymentMethodData && (
        <StripePaymentForm
          isOpen={true}
          onClose={() => {
            toast.error("Payment cancelled");
            // Close Stripe form and return to payment method selection
            setPaymentMethodData(null);
            setShowPaymentMethodModal(true);
          }}
          stripePublishableKey={paymentMethodData.stripe_publishable_key}
          clientSecret={paymentMethodData.stripe_client_id}
          amount={paymentMethodData.price}
          currency={paymentMethodData.currency}
          onSuccess={() => {
            // Backend webhook handles payment confirmation, so just navigate to my-tickets
            toast.success("Payment successful!");
            onClose(); // Close the checkout modal
            navigate(getMyTicketsUrl(organizationSlug));
          }}
        />
      )}
    </AnimatePresence>
  );
}

// Stripe Payment Form Component
interface StripePaymentFormProps {
  isOpen: boolean;
  onClose: () => void;
  stripePublishableKey: string;
  clientSecret: string;
  amount: number;
  currency: string;
  onSuccess: () => void;
}

function StripePaymentForm({
  isOpen,
  onClose,
  stripePublishableKey,
  clientSecret,
  amount,
  currency,
  onSuccess,
}: StripePaymentFormProps) {
  const stripePromise = loadStripe(stripePublishableKey);
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen) return null;

  return (
    <Elements stripe={stripePromise}>
      <StripeCheckoutForm
        clientSecret={clientSecret}
        amount={amount}
        currency={currency}
        onSuccess={onSuccess}
        onClose={onClose}
        isProcessing={isProcessing}
        setIsProcessing={setIsProcessing}
      />
    </Elements>
  );
}

function StripeCheckoutForm({
  clientSecret,
  amount,
  currency,
  onSuccess,
  onClose,
  isProcessing,
  setIsProcessing,
}: {
  clientSecret: string;
  amount: number;
  currency: string;
  onSuccess: () => void;
  onClose: () => void;
  isProcessing: boolean;
  setIsProcessing: (value: boolean) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement)!,
          },
        }
      );

      if (error) {
        toast.error(error.message || "Payment failed");
        setIsProcessing(false);
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        toast.success("Payment successful!");
        onSuccess();
      }
    } catch (error: any) {
      toast.error(error.message || "Payment failed");
      setIsProcessing(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900">Complete Payment</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-lg font-semibold text-gray-900 mb-1">
            Amount: {currency} {amount.toLocaleString()}
          </p>
          <p className="text-sm text-gray-600">Enter your card details below</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-4 border-2 border-gray-200 rounded-lg">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#424770",
                    "::placeholder": {
                      color: "#aab7c4",
                    },
                  },
                  invalid: {
                    color: "#9e2146",
                  },
                },
              }}
            />
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 px-4 border-2 border-gray-200 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!stripe || isProcessing}
              className="flex-1 py-3 px-4 bg-[#17265a] text-white rounded-lg hover:bg-[#0f1a3f] transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                `Pay ${currency} ${amount.toLocaleString()}`
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
